import { useEffect, useRef, useState } from "react";
import PageWrapper from "../components/PageWrapper";
import SearchBar from "../components/SearchBar";
import Colors from '../styles/Colors.json';
import './EmployeeViewV1.css';
import EmployeeViewManager from "../managers/EmployeeViewManager";
import { EmployeeInfo, RoleInfo } from "../interfaces/EmployeeInfoInterface";
import { useSiteContext } from "../SiteContext";
import ToggleSwitch from "../components/ToggleSwitch";
import ActionButton from "../components/ActionButton";
import EmployeeSyncModal from "../components/EmployeeSyncModal";
import EnrollmentPhotoModal from "../components/EnrollmentPhotoModal";
import EmployeeRoleEditModal from "../components/EmployeeRoleEditModal";
import EmployeePWClassificationModal from "../components/EmployeePWClassificationModal";
import {applyOffsetToUTC, applyOffsetToUTCDateTime} from "../util/DateAndTimeUtil";
import config from "../configLoader";
import { consumeStashedDeepLink, peekStashedDeepLink } from "../interfaces/DeepLinkInterface";

const EmployeeViewV1: React.FC = () => {
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [selectedSuper, setSelectedSuper] = useState<string>("ALL");
    const [selectedLocation, setSelectedLocation] = useState<string>("ALL");
    const [selectedSort, setSelectedSort] = useState<string>("ID");
    const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
    const [data, setData] = useState<EmployeeInfo[] | null>(null);
    const [sortedData, setSortedData] = useState<EmployeeInfo[] | null>(null);
    const [isActive, setIsActive] = useState<boolean>(true);
    const [supervisorIDs, setSupervisorIDs] = useState<string[]>([]);
    const [supervisorEmpIDs, setSupervisorEmpIDs] = useState<number[]>([]);
    const [supervisors, setSupervisors] = useState<EmployeeInfo[]>([]);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage, setItemsPerPage] = useState<number>(30);
    
    // State for managing modal visibility
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [enrollmentEmployee, setEnrollmentEmployee] = useState<EmployeeInfo | null>(null);
    const [employeeRoles, setEmployeeRoles] = useState<RoleInfo[]>([]);
    const [roleEditEmployee, setRoleEditEmployee] = useState<EmployeeInfo | null>(null);
    const [pwClassificationModalEmployee, setPWClassificationModalEmployee] = useState<EmployeeInfo | null>(null);

    const { setLoading, createPopup, locations, userRole, translator, pendingDeepLink } = useSiteContext();

    // Deep link `?page=employeeView&photo={altEmployeeID}` (TAS-568 failure email). The v1
    // shell (NPMech prod) renders this page, so it opens the legacy photo modal once the
    // roster has loaded. Consumed once.
    const photoDeepLinkConsumed = useRef(false);
    useEffect(() => {
        const canSeePhotos =
            config.showPhotoPages &&
            (userRole.admin || userRole.viewEmployeeEnrollmentPhotos || userRole.editEmployeeEnrollmentPhotos);
        if (photoDeepLinkConsumed.current || !canSeePhotos || !data) return;
        const stashed = peekStashedDeepLink();
        const link = pendingDeepLink?.photo ? pendingDeepLink : stashed?.photo ? stashed : null;
        if (!link?.photo) return;
        photoDeepLinkConsumed.current = true;
        if (stashed?.photo) consumeStashedDeepLink();
        const emp = data.find((e) => e.altEmployeeID === link.photo);
        if (!emp) return;
        if (emp.active !== isActive) setIsActive(emp.active);
        setEnrollmentEmployee(emp);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot; isActive read at consumption time
    }, [data, pendingDeepLink, userRole]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await EmployeeViewManager.getEmployees();
            setData(response); // Set fetched data
            grabSupervisors(); // Extract supervisor data
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchEmployeeRoles = async () => {
        try {
            const roles = await EmployeeViewManager.getEmployeeRoles();
            setEmployeeRoles(roles);
        } catch (error) {
            console.error("Error fetching employee roles:", error);
        }
    };

    // Fetch employee data on component mount
    useEffect(() => {
        fetchData();
        fetchEmployeeRoles();
    }, []);

    // Update supervisor data when `data` or `selectedLocation` changes
    useEffect(() => {
        if (!data) return;
        grabSupervisors();
    }, [data, selectedLocation]);

    // Reset supervisor to "ALL" when location changes
    useEffect(() => {
        setSelectedSuper("ALL");
    }, [selectedLocation]);

    // Sort and filter data when supervisor, location, or active status changes
    useEffect(() => {
        sortData(selectedSuper, selectedLocation, isActive);
        if (currentPage == 0 || currentPage > totalPages) setCurrentPage(1);
    }, [selectedSuper, selectedLocation, isActive, supervisorIDs, supervisors, selectedSort, sortDirection]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    const getSupervisorName = (supervisorID: number): string => {
        if (supervisorID === 0) return "-"; // Handle case where there's no supervisor
        
        // Find the supervisor in the supervisors array
        const supervisor = supervisors.find(sup => sup.employeeID === supervisorID);
        
        if (supervisor) {
            return `${supervisor.firstName} ${supervisor.lastName}`;
        }
        
        return "-"; // Default if supervisor not found
    };

    const getRoleDisplay = (roleID: number): string => {
        const role = employeeRoles.find((item) => item.roleID === roleID);
        if (!role) {
            return roleID ? roleID.toString() : "-";
        }
        return `${role.name} (${role.roleID})`;
    };

    const activeEmployeeRoles = employeeRoles.filter((role) => role.active);

    const formatPWClassification = (emp: EmployeeInfo) =>
        emp.pwClassification?.name
            ? `${emp.pwClassification.name}${emp.pwClassification.altPWClassificationID ? ` (${emp.pwClassification.altPWClassificationID})` : ""}`
            : "—";

    // Function to extract and organize supervisor data
    const grabSupervisors = () => {
        if (!data) return;

        // Extract unique supervisor IDs from all data (to identify who is a supervisor)
        const uniqueSupervisorIDs: number[] = [];
        data.forEach((employee) => {
            if (!uniqueSupervisorIDs.includes(employee.supervisorID)) {
                uniqueSupervisorIDs.push(employee.supervisorID);
            }
        });

        // Sort supervisor IDs
        const sortedSupervisorIDs = uniqueSupervisorIDs.sort((a, b) => a - b);
        setSupervisorEmpIDs(sortedSupervisorIDs);

        // Map supervisor IDs to their corresponding altEmployeeID or employeeID
        const idList: string[] = ["0"]; // Start with "0" for the "ALL" option
        const uniqueSupervisors: EmployeeInfo[] = [];

        // Filter employees who are supervisors and belong to the selected location
        const matchingEmployees = data.filter((employee) => {
            // Must be a supervisor (employeeID appears in supervisor IDs list)
            if (!uniqueSupervisorIDs.includes(employee.employeeID)) {
                return false;
            }
            // If location is selected, only include supervisors from that location
            if (selectedLocation !== "ALL") {
                const locationIDNum = parseInt(selectedLocation);
                return employee.locationID === locationIDNum;
            }
            return true;
        });

        // Add supervisor IDs and details to the lists
        matchingEmployees.forEach((employee) => {
            if (employee.altEmployeeID) {
                idList.push(employee.altEmployeeID);
            } else {
                idList.push(employee.employeeID.toLocaleString());
            }
            uniqueSupervisors.push(employee);
        });

        console.log("ID List: ", idList);
        setSupervisorIDs(idList); // Set supervisor IDs
        setSupervisors(uniqueSupervisors); // Set supervisor details
    };

    // Function to sort and filter employee data
    const sortData = async (superID: string, locationID: string, active: boolean) => {
        if (!data) return;
        setLoading(true);

        let newData = data;

        // Filter data based on supervisor, location, and active status
        newData = newData.filter((employee) => {
            const employeeSupervisorID = employee.supervisorID;
            
            // Match supervisor logic
            let matchesSupervisor = true;
            if (superID !== "ALL") {
                if (superID === "0") {
                    // Match employees with supervisorID === 0
                    matchesSupervisor = employeeSupervisorID === 0;
                } else {
                    // Find the supervisor object that matches the selected superID (altEmployeeID)
                    const selectedSupervisor = supervisors.find(sup => sup.altEmployeeID === superID);
                    if (selectedSupervisor) {
                        matchesSupervisor = employeeSupervisorID === selectedSupervisor.employeeID;
                    } else {
                        matchesSupervisor = false;
                    }
                }
            }
            
            const matchesLocation = 
                locationID === "ALL" || 
                employee.locationID === parseInt(locationID);
            const matchesActiveStatus = employee.active === active;
            return matchesSupervisor && matchesLocation && matchesActiveStatus;
        });

        // Sort filtered data by altEmployeeID or employeeID
        newData = newData.sort((a, b) => {
            let aAttribute;
            let bAttribute;

            switch(selectedSort) {
                case "Name":
                    aAttribute = `${a.firstName} ${a.lastName}`;
                    bAttribute = `${b.firstName} ${b.lastName}`;
                    break;
                case "ID":
                    aAttribute = a.altEmployeeID ?? a.employeeID.toLocaleString();
                    bAttribute = b.altEmployeeID ?? b.employeeID.toLocaleString();
                    break;
                case "Phone":
                    aAttribute = a.phoneNumber ?? '';
                    bAttribute = b.phoneNumber ?? '';
                    break;
                case "JobTitle":
                    aAttribute = a.jobTitle ?? '';
                    bAttribute = b.jobTitle ?? '';
                    break;
                case "Role":
                    aAttribute = a.roleID?.toString() ?? '';
                    bAttribute = b.roleID?.toString() ?? '';
                    break;
                case "LastLogin":
                    aAttribute = applyOffsetToUTCDateTime(a.lastLoginDate, a.lastLoginDateOffset) ?? '';
                    bAttribute = applyOffsetToUTCDateTime(b.lastLoginDate, b.lastLoginDateOffset) ?? '';
                    break;
                case "Supervisor":
                    const aSuper = grabSuperAlt(a.supervisorID);
                    const bSuper = grabSuperAlt(b.supervisorID);
                    aAttribute = aSuper;
                    bAttribute = bSuper;
                    break;
                case "PWClassification":
                    aAttribute = a.pwClassification?.name ?? "";
                    bAttribute = b.pwClassification?.name ?? "";
                    break;
                default:
                    aAttribute = a.altEmployeeID ?? a.employeeID.toLocaleString();
                    bAttribute = b.altEmployeeID ?? b.employeeID.toLocaleString();
            }

            const comparison = aAttribute.localeCompare(bAttribute);
            return sortDirection === "asc" ? comparison : -comparison;
        });

        setSortedData(newData);
        setLoading(false);
    };

    const handleColumnSort = (column: string) => {
        if (selectedSort === column) {
            setSortDirection(prev => prev === "asc" ? "desc" : "asc");
        } else {
            setSelectedSort(column);
            setSortDirection("asc");
        }
    };

    const grabSuperAlt = (empID: number): string => {
        const index = supervisorEmpIDs.indexOf(empID);
        return supervisorIDs[index] ?? "";
    };

    // Toggle active/inactive filter
    const handleSwitchToggle = () => {
        setIsActive((prev) => !prev);
    };

    // Filter employees based on search term
    const filteredEmployees = sortedData?.filter((employee) => {
        if (searchTerm.trim() === "") {
            return true; // Return all employees if search term is empty
        }
        const searchTermLower = searchTerm.toLowerCase();

        // Search by name (if search term contains letters)
        if (/[a-zA-Z]/.test(searchTerm)) {
            const fullName = `${employee.firstName} ${employee.lastName}`.toLowerCase();
            return fullName.includes(searchTermLower);
        }

        // Search by employee ID or phone number (if search term is numeric)
        if (/^\d+$/.test(searchTerm)) {
            const empID = employee.altEmployeeID ? 
                employee.altEmployeeID.toString().includes(searchTerm) :
                employee.employeeID.toString().includes(searchTerm);
            const phoneNumberMatch = employee.phoneNumber ? 
                employee.phoneNumber.replace(/\D/g, '').includes(searchTerm) : 
                false;
            return empID || phoneNumberMatch;
        }

        return false; // Exclude if no match
    }) || [];

    // Pagination logic: calculate indices for current page
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredEmployees.slice(indexOfFirstItem, indexOfLastItem);

    // Function to change page
    const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

    // Calculate total pages for pagination
    const totalPages = Math.ceil(filteredEmployees.length / itemsPerPage);
    const maxVisibleButtons = 10; // Maximum number of visible pagination buttons

    // Function to render pagination buttons
    const renderPaginationButtons = () => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages); // Ensure current page is within valid range
        }
        const buttons = [];
        let startPage, endPage;

        // Determine the range of visible page buttons
        if (totalPages <= maxVisibleButtons) {
            startPage = 1;
            endPage = totalPages;
        } else {
            const halfVisible = Math.floor(maxVisibleButtons / 2);
            if (currentPage <= halfVisible) {
                startPage = 1;
                endPage = maxVisibleButtons;
            } else if (currentPage + halfVisible >= totalPages) {
                startPage = totalPages - maxVisibleButtons + 1;
                endPage = totalPages;
            } else {
                startPage = currentPage - halfVisible;
                endPage = currentPage + halfVisible;
            }
        }

        // Add "Previous" button
        buttons.push(
            <button
                key="prev"
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1 || totalPages <= 1}
            >
                Previous
            </button>
        );

        // Add "..." for overflow on the left
        if (startPage > 1) {
            buttons.push(
                <button key="start-ellipsis" disabled>
                    ...
                </button>
            );
        }

        // Add page number buttons
        for (let i = startPage; i <= endPage; i++) {
            buttons.push(
                <button
                    key={i}
                    onClick={() => paginate(i)}
                    className={currentPage === i ? "active" : ""}
                >
                    {i}
                </button>
            );
        }

        // Add "..." for overflow on the right
        if (endPage < totalPages) {
            buttons.push(
                <button key="end-ellipsis" disabled>
                    ...
                </button>
            );
        }

        // Add "Next" button
        buttons.push(
            <button
                key="next"
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
            >
                Next
            </button>
        );

        return buttons;
    };

    // Function to format phone numbers
    function formatPhoneNumber(phoneNumber: string): string {
        const countryCode = phoneNumber.slice(0, 2);
        const areaCode = phoneNumber.slice(2, 5);
        const firstPart = phoneNumber.slice(5, 8);
        const secondPart = phoneNumber.slice(8, 12);
        return `${countryCode} (${areaCode}) ${firstPart}-${secondPart}`;
    }

    // Sync logic moved here
    const handleEmployeeSync = async (employeeAltID: string, employeeAltLocationID: string, name?: string) => {
        if (!employeeAltID) return;  // If no AltID, exit

        setLoading(true);
        try {
            const response = await EmployeeViewManager.syncEmployeeData(employeeAltID, employeeAltLocationID);
            console.log("Data Sync:", response);
            
            // Format the popup message based on the new response structure
            let popupMessage = response.message || (response.success ? "Sync completed successfully!" : "Sync failed! Please try again.");
            
            // Add updates if they exist
            if (response.updates && response.updates.length > 0) {
                popupMessage += "\n\nUpdates:\n" + response.updates.join("\n");
            }
            
            createPopup(`Data Sync for ${name ?? ""} (${employeeAltID})`, popupMessage);
        } catch (error) {
            console.error("Error during sync:", error);
            createPopup(`Data Sync for ${name ?? ""} (${employeeAltID})`, "Sync failed! Please try again.");
        } finally {
            fetchData();
            setLoading(false);
        }
    };

    const openSyncModal = () => {
        setIsModalOpen(true);
    };

    const closeSyncModal = () => {
        setIsModalOpen(false);
    };

    const openRoleEditModal = (employee: EmployeeInfo) => {
        setRoleEditEmployee(employee);
    };

    const closeRoleEditModal = () => {
        setRoleEditEmployee(null);
    };

    const handleRoleSave = async (employeeID: number, roleID: number) => {
        setLoading(true);
        try {
            await EmployeeViewManager.updateEmployeeRole(employeeID, roleID);
            closeRoleEditModal();
            await fetchData();
            createPopup("Role Update", "Employee role updated successfully");
        } catch (error: any) {
            const status = error?.response?.status;
            if (status === 403) {
                createPopup("Role Update", "Not authorized to edit employee roles");
            } else if (status === 400) {
                createPopup("Role Update", "Invalid employeeID or roleID");
            } else if (status === 404) {
                createPopup("Role Update", "Employee not found");
            } else {
                createPopup("Role Update", "Error updating employee role");
            }
        } finally {
            setLoading(false);
        }
    };


    return (
        <PageWrapper title="Employee View" backgroundColor={Colors.White}>
            {(userRole.admin || userRole.payroll || userRole.hr) && (
                <div className="employee-view-location-row">
                    <div className="employee-view-sort-by">
                        <label>Location: </label>
                        <select value={selectedLocation} onChange={(e) => setSelectedLocation(e.target.value)}>
                            <option key={0} value={"ALL"}>
                                ALL
                            </option>
                            {locations?.filter(location => location.testLocation === false).sort((a, b) => {
                                // Sort by altLocationID if available, otherwise by name
                                const aKey = a.altLocationID || a.name;
                                const bKey = b.altLocationID || b.name;
                                return aKey.localeCompare(bKey);
                            }).map((location) => (
                                <option key={location.locationID} value={location.locationID.toString()}>
                                    {location.altLocationID ? `${location.altLocationID} - ${location.name}` : location.name}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            )}
            <div className="employee-view-top-row">
                <div className="employee-view-sort-by">
                    <label>Supervisor: </label>
                    <select value={selectedSuper} onChange={(e) => setSelectedSuper(e.target.value)}>
                        <option key={0} value={"ALL"}>
                            ALL
                        </option>
                        <option key={1} value={"0"}>
                            0 - Supervisors
                        </option>
                        {supervisors?.map((supervisor, index) => (
                            <option key={index} value={supervisor.altEmployeeID}>
                                {supervisor.altEmployeeID + " - " + supervisor.firstName + " " + supervisor.lastName}
                            </option>
                        ))}
                    </select>
                </div>
                <ToggleSwitch
                    label="Active:"
                    checked={isActive}
                    onChange={() => setIsActive((prev) => !prev)}
                />
                <SearchBar
                    placeholder={"Search Employees"}
                    value={searchTerm}
                    onChange={setSearchTerm}
                    style={{
                        width: '100%',
                        boxSizing: 'border-box',
                        overflowX: 'visible'
                    }}
                />
                <ActionButton title={"Sync New Employee"} onAction={openSyncModal} isEnabled={true} />
            </div>
            <div className="employee-view-table">
                <table>
                    <thead>
                        <tr className="employee-view-table-row-blue">
                            <th onClick={() => handleColumnSort("Name")}>
                                Name {selectedSort === "Name" && (sortDirection === "asc" ? "↑" : "↓")}
                            </th>
                            <th onClick={() => handleColumnSort("ID")}>
                                ID {selectedSort === "ID" && (sortDirection === "asc" ? "↑" : "↓")}
                            </th>
                            <th onClick={() => handleColumnSort("Phone")}>
                                Cell Phone {selectedSort === "Phone" && (sortDirection === "asc" ? "↑" : "↓")}
                            </th>
                            <th onClick={() => handleColumnSort("JobTitle")}>
                                Job Title {selectedSort === "JobTitle" && (sortDirection === "asc" ? "↑" : "↓")}
                            </th>
                            <th onClick={() => handleColumnSort("Role")}>
                                Role {selectedSort === "Role" && (sortDirection === "asc" ? "↑" : "↓")}
                            </th>
                            <th onClick={() => handleColumnSort("LastLogin")}>
                                Last Login {selectedSort === "LastLogin" && (sortDirection === "asc" ? "↑" : "↓")}
                            </th>
                            <th onClick={() => handleColumnSort("Supervisor")}>
                                Supervisor {selectedSort === "Supervisor" && (sortDirection === "asc" ? "↑" : "↓")}
                            </th>
                            {config.showPrevailingWagePages && (
                                <th onClick={() => handleColumnSort("PWClassification")}>
                                    PW Classification {selectedSort === "PWClassification" && (sortDirection === "asc" ? "↑" : "↓")}
                                </th>
                            )}
                            {(userRole.admin || userRole.viewEmployeeEnrollmentPhotos || userRole.editEmployeeEnrollmentPhotos) && config.showPhotoPages && (
                                <th>{translator.translate("enrollmentPhotoColumn")}</th>
                            )}
                            <th>Sync</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentItems?.map((employee, index) => (
                            <tr key={index} className={index % 2 === 1 ? "employee-view-table-row-blue" : "employee-view-table-row-white"}>
                                <td>{employee.firstName + " " + (employee.middleName ? employee.middleName + " " : "") + employee.lastName}</td>
                                <td>{employee.altEmployeeID ?? '-'}</td>
                                <td>{employee.phoneNumber ? formatPhoneNumber(employee.phoneNumber) : "-"}</td>
                                <td>{employee.jobTitle ?? '-'}</td>
                                <td>
                                    {userRole.editEmployeeRole ? (
                                        <button
                                            type="button"
                                            className="employee-role-link"
                                            onClick={() => openRoleEditModal(employee)}
                                        >
                                            {getRoleDisplay(employee.roleID)}
                                        </button>
                                    ) : (
                                        getRoleDisplay(employee.roleID)
                                    )}
                                </td>
                                <td>{applyOffsetToUTCDateTime(employee.lastLoginDate, employee.lastLoginDateOffset) ?? '-'}</td>
                                <td>{getSupervisorName(employee.supervisorID)}</td>
                                {config.showPrevailingWagePages && (
                                    <td
                                        onClick={userRole.editEmployeeRole ? () => setPWClassificationModalEmployee(employee) : undefined}
                                        style={userRole.editEmployeeRole ? { cursor: "pointer" } : undefined}
                                    >
                                        {employee.pwClassification?.name ? (
                                            userRole.editEmployeeRole ? (
                                                <button
                                                    type="button"
                                                    className="employee-role-link"
                                                    onClick={(e) => { e.stopPropagation(); setPWClassificationModalEmployee(employee); }}
                                                >
                                                    {formatPWClassification(employee)}
                                                </button>
                                            ) : (
                                                formatPWClassification(employee)
                                            )
                                        ) : (
                                            <span style={{ color: "#276ef1" }}>—</span>
                                        )}
                                    </td>
                                )}
                                {(userRole.admin || userRole.viewEmployeeEnrollmentPhotos || userRole.editEmployeeEnrollmentPhotos) && config.showPhotoPages && (
                                    <td>
                                        <button
                                            type="button"
                                            className="edit-role-button"
                                            title={translator.translate("enrollmentPhotoOpen")}
                                            onClick={() => setEnrollmentEmployee(employee)}
                                        >
                                            {translator.translate("enrollmentPhotoColumn")}
                                        </button>
                                    </td>
                                )}
                                <td>
                                    <button 
                                        className="edit-role-button"
                                        onClick={() => handleEmployeeSync(employee.altEmployeeID, employee.altLocationID, (employee.firstName + " " + employee.lastName))}
                                    >
                                        ↻
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="pagination">
                {renderPaginationButtons()}
            </div>

            {/* Employee Sync Modal */}
            <EmployeeSyncModal
                isOpen={isModalOpen}
                onClose={closeSyncModal}
                onSubmit={handleEmployeeSync}
                config={config}  // Pass the config prop
            />

            {roleEditEmployee && (
                <EmployeeRoleEditModal
                    isOpen={!!roleEditEmployee}
                    employee={roleEditEmployee}
                    roles={activeEmployeeRoles}
                    onClose={closeRoleEditModal}
                    onSave={handleRoleSave}
                />
            )}

            {pwClassificationModalEmployee && (
                <EmployeePWClassificationModal
                    isOpen={true}
                    employee={pwClassificationModalEmployee}
                    onClose={() => setPWClassificationModalEmployee(null)}
                    onSaved={async () => {
                        setPWClassificationModalEmployee(null);
                        await fetchData();
                    }}
                />
            )}

            {(userRole.admin || userRole.viewEmployeeEnrollmentPhotos || userRole.editEmployeeEnrollmentPhotos) && config.showPhotoPages && enrollmentEmployee && (
                <EnrollmentPhotoModal
                    isOpen={!!enrollmentEmployee}
                    onClose={() => setEnrollmentEmployee(null)}
                    employeeId={enrollmentEmployee.employeeID}
                    employeeLabel={`${enrollmentEmployee.firstName} ${enrollmentEmployee.lastName}`}
                    canEdit={userRole.admin || !!userRole.editEmployeeEnrollmentPhotos}
                />
            )}
        </PageWrapper>
    );
};

export default EmployeeViewV1;